/**
 * Targeting Evaluator for Admin Bar
 *
 * Evaluates targeting conditions and updates the store.
 * Reuses the frontend targeting evaluation logic.
 *
 * Server-side conditions have `serverResult` attached directly to each condition
 * by PHP. The condition-registry uses these pre-computed results automatically,
 * so the evaluation results include both server and client condition results.
 */

import { evaluateTargeting, createBrowserContext } from '../frontend/targeting';
import type { AdminBarFeed } from './index';
import type { AdminBarStore } from './store';

/**
 * Evaluate targeting conditions for all feeds and update the store.
 */
export async function evaluateAllFeeds(
	feeds: AdminBarFeed[],
	store: AdminBarStore
): Promise<void> {
	const context = createBrowserContext();

	for (const feed of feeds) {
		// Skip feeds without targeting groups
		if (!feed.targetingGroups?.length) {
			// No targeting = show to everyone
			store.updateEvaluationResults(feed.feedId, []);
			continue;
		}

		// Evaluate all conditions.
		// Server-side conditions with `serverResult` will use the pre-computed result.
		// Client-side conditions will be evaluated in JS.
		const result = await evaluateTargeting(feed.targetingGroups, context);

		// Update the store with the evaluation results
		store.updateEvaluationResults(feed.feedId, result.groupResults);
	}
}

/**
 * Re-evaluate conditions for a single feed.
 * Useful if conditions might change (e.g., time-based conditions).
 */
export async function reevaluateFeed(
	feed: AdminBarFeed,
	store: AdminBarStore
): Promise<void> {
	if (!feed.targetingGroups?.length) {
		store.updateEvaluationResults(feed.feedId, []);
		return;
	}

	const context = createBrowserContext();
	const result = await evaluateTargeting(feed.targetingGroups, context);
	store.updateEvaluationResults(feed.feedId, result.groupResults);
}
